/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";

// react-router-dom
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Overview page components
import Header from "layouts/profile/components/Header";

function Overview() {
  const location = useLocation();
  const [apiUser, setApiUser] = useState(null);

  const user = (() => {
    try {
      const raw = localStorage.getItem("dsf_user");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  })();

  const selectedUser = location?.state?.user || null;
  const storedUser = (() => {
    try {
      const raw = localStorage.getItem("dsf_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const authToken =
    localStorage.getItem("dsf_token") ||
    localStorage.getItem("token") ||
    storedUser?.token ||
    storedUser?.accessToken ||
    localStorage.getItem("accessToken") ||
    "";

  useEffect(() => {
    const fetchProfile = async () => {
      if (!authToken) {
        setApiUser(null);
        return;
      }

      try {
        const apiBaseUrl =
          process.env.REACT_APP_LOGIN_API_URL || "https://dsf-api.classicprogrammers.com/api/v1";
        const normalizedApiBaseUrl = apiBaseUrl.replace(/\/+$/, "");
        if (authToken) {
          axios.defaults.headers.common.Authorization = `Bearer ${authToken}`;
        }
        const response = await axios.get(`${normalizedApiBaseUrl}/auth/me`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });

        const data = response?.data || {};
        const resolvedUser = data.data || data.user || data;
        setApiUser(resolvedUser);
      } catch {
        setApiUser(null);
      }
    };

    fetchProfile();
  }, [authToken]);

  const profileUser = {
    name: apiUser?.name || selectedUser?.name || user?.name || "User",
    email: apiUser?.email || selectedUser?.email || user?.email || "",
    role: apiUser?.role || selectedUser?.role || user?.role || "User",
  };

  const normalizeTags = (value) => {
    if (!Array.isArray(value)) return [];

    return value
      .map((tag) => {
        if (typeof tag === "string") return tag;
        if (tag && typeof tag === "object") {
          return tag.name || tag.description || tag._id || "";
        }
        return "";
      })
      .filter(Boolean);
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const details = {
    lastActive: formatDateTime(apiUser?.lastActiveAt || selectedUser?.lastActiveAt),
    joinDate: formatDateTime(apiUser?.createdAt || selectedUser?.createdAt),
    tags: normalizeTags(apiUser?.tags || selectedUser?.tags),
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <Header user={profileUser}>
        <MDBox px={{ xs: 1, sm: 2 }} pb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ width: "100%", overflow: "hidden" }}>
                <MDBox
                  mx={{ xs: 1, sm: 2 }}
                  mt={0}
                  py={{ xs: 1.75, sm: 2.5 }}
                  px={{ xs: 2, sm: 2.5 }}
                  variant="contained"
                  borderRadius="lg"
                  coloredShadow="none"
                  sx={{
                    background: "rgba(229, 10, 70, 1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    minHeight: { xs: "3rem", sm: "3.5rem" },
                  }}
                >
                  <MDTypography variant="h6" color="white" sx={{ lineHeight: 1.1 }}>
                    User Details
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="white"
                    sx={{ opacity: 0.9, lineHeight: 1.1 }}
                  >
                    Overview
                  </MDTypography>
                </MDBox>
                <MDBox p={{ xs: 2, sm: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6} lg={4}>
                      <MDTypography variant="caption" color="text">
                        Name
                      </MDTypography>
                      <MDTypography
                        variant="h6"
                        fontWeight="medium"
                        color="text"
                        display="block"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {profileUser.name}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                      <MDTypography variant="caption" color="text">
                        Email
                      </MDTypography>
                      <MDTypography
                        variant="h6"
                        fontWeight="medium"
                        color="text"
                        display="block"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {profileUser.email || "—"}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                      <MDTypography variant="caption" color="text">
                        Role
                      </MDTypography>
                      <MDTypography
                        variant="h6"
                        fontWeight="medium"
                        color="text"
                        display="block"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {profileUser.role}
                      </MDTypography>
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                      <MDTypography variant="caption" color="text">
                        Last active
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="medium" color="text" display="block">
                        {details.lastActive}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                      <MDTypography variant="caption" color="text">
                        Join date
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="medium" color="text" display="block">
                        {details.joinDate}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                      <MDTypography variant="caption" color="text">
                        Tags
                      </MDTypography>
                      <MDBox mt={1} display="flex" flexWrap="wrap" gap={1}>
                        {details.tags.length ? (
                          details.tags.map((t, idx) => (
                            <Chip
                              key={`${t}-${idx}`}
                              label={t}
                              size="small"
                              sx={{
                                height: "1.6rem",
                                backgroundColor: "rgba(17, 24, 39, 0.06)",
                                color: "#344767",
                                fontWeight: 600,
                                "& .MuiChip-label": { px: 1.25 },
                              }}
                            />
                          ))
                        ) : (
                          <MDTypography variant="caption" color="text">
                            —
                          </MDTypography>
                        )}
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </Header>
    </DashboardLayout>
  );
}

export default Overview;
